import { useCallback, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Task, DocumentRetrievalExclusionReason } from "@common/types/task";
import { FiltersTypes } from "@common/types/filters/general";
import { useUpdateViewMutation } from "@api/view.api";
import { tasksQueryKeys } from "@api/task.api";
import { useViewScope } from "@hooks/view/useViewScope";
import { useScope } from "@state/scopeContext.lib";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { generateMonthlySeeds } from "./documentRetrievalHelpers";

export function useDocumentRetrievalExclusion(selectedTask: Task) {
    const queryClient = useQueryClient();
    const { activeWorkspaceKey } = useScope();
    const viewScope = useViewScope();
    const updateView = useUpdateViewMutation(viewScope);
    const { toast } = useToaster();

    const viewFilters = selectedTask.view?.filters;
    const periodStart = viewFilters?.periodStart ?? "";
    const periodEnd = viewFilters?.periodEnd ?? "";

    const orderedMonthKeys = useMemo(
        () => generateMonthlySeeds(periodStart, periodEnd).map((m) => m.key),
        [periodStart, periodEnd],
    );

    const excludedPeriods = useMemo(
        () => viewFilters?.excludedPeriods ?? {},
        [viewFilters?.excludedPeriods],
    );

    const excludedPeriodsRef = useRef(excludedPeriods);
    excludedPeriodsRef.current = excludedPeriods;

    const taskDetailKey = tasksQueryKeys.detail(
        activeWorkspaceKey,
        selectedTask.id,
    );

    const updateExcludedPeriods = useCallback(
        (nextExcluded: Record<string, DocumentRetrievalExclusionReason>) => {
            const view = selectedTask.view;
            if (!view) {
                return;
            }

            excludedPeriodsRef.current = nextExcluded;

            const updatedFilters: FiltersTypes = {
                ...viewFilters,
                excludedPeriods:
                    Object.keys(nextExcluded).length > 0
                        ? nextExcluded
                        : undefined,
            };

            queryClient.setQueryData(taskDetailKey, (old: Task | undefined) => {
                if (!old?.view) {
                    return old;
                }
                return {
                    ...old,
                    view: { ...old.view, filters: updatedFilters },
                };
            });

            updateView.mutate(
                {
                    id: view.id,
                    type: view.type,
                    columns: view.columns,
                    filters: updatedFilters,
                    name: view.name,
                },
                {
                    onSuccess: () => {
                        queryClient.invalidateQueries({
                            queryKey: taskDetailKey,
                        });
                    },
                    onError: () => {
                        excludedPeriodsRef.current = excludedPeriods;
                        queryClient.invalidateQueries({
                            queryKey: taskDetailKey,
                        });
                        toast("Failed to update period status");
                    },
                },
            );
        },
        [
            selectedTask.view,
            viewFilters,
            updateView,
            queryClient,
            taskDetailKey,
            excludedPeriods,
            toast,
        ],
    );

    const handleExcludeMany = useCallback(
        (monthKeys: string[], reason: DocumentRetrievalExclusionReason) => {
            if (monthKeys.length === 0) {
                return;
            }

            const next = { ...excludedPeriodsRef.current };
            for (const monthKey of monthKeys) {
                next[monthKey] = reason;
            }
            updateExcludedPeriods(next);
        },
        [updateExcludedPeriods],
    );

    const handleExclude = useCallback(
        (monthKey: string, reason: DocumentRetrievalExclusionReason) => {
            if (reason === DocumentRetrievalExclusionReason.ACCOUNT_CLOSED) {
                const startIndex = orderedMonthKeys.indexOf(monthKey);
                const closedKeys =
                    startIndex >= 0
                        ? orderedMonthKeys.slice(startIndex)
                        : [monthKey];
                handleExcludeMany(closedKeys, reason);
                return;
            }

            handleExcludeMany([monthKey], reason);
        },
        [handleExcludeMany, orderedMonthKeys],
    );

    const handleClearExclusion = useCallback(
        (monthKey: string) => {
            const next = { ...excludedPeriodsRef.current };
            const wasAccountClosed =
                next[monthKey] ===
                DocumentRetrievalExclusionReason.ACCOUNT_CLOSED;

            delete next[monthKey];

            if (wasAccountClosed) {
                for (const key of orderedMonthKeys) {
                    if (
                        next[key] ===
                        DocumentRetrievalExclusionReason.ACCOUNT_CLOSED
                    ) {
                        delete next[key];
                    }
                }
            }

            updateExcludedPeriods(next);
        },
        [updateExcludedPeriods, orderedMonthKeys],
    );

    return {
        excludedPeriods,
        handleExclude,
        handleExcludeMany,
        handleClearExclusion,
    };
}
